// src/pages/HomePage.jsx
import React, { useEffect, useMemo, useState } from "react";

// ---- Styles (unchanged) ----
import "../styles/HomePage.css";
import "../styles/Shop.css";
import "../styles/BasketSidebar.css";

// ---- UI Components ----
import BottomSheet from "../components/BottomSheet";
import Cart from "../components/Cart";
import StoreInfo from "../components/StoreInfo";
import ItemModal from "../components/ItemModal";
import AddressGateModal from "../components/AddressGateModal";

// ---- Data services (your existing abstractions) ----
import {
  getAllItems, // -> Promise<Item[]>
  getCategoryMap, // -> Promise<Record<categoryId, namePl>>
  getAllIngredients, // -> Promise<Ingredient[]>
  getShopById, // -> Promise<Shop>
} from "../services";

// ---- Utils ----
import { formatOpeningHoursToday } from "../utils/formatOpeningHours";
import { isShopOpenNow } from "../utils/checkShopOpen";

// ---- Zustand stores ----
import useCheckoutStore from "../store/useCheckoutStore";
import useBottomSheetStore from "../store/useBottomSheetStore";
import useShopStore from "../store/useShopStore";

// ---- Extras helpers (filtered by SHOP_ID inside the service) ----
import {
  fetchExtrasByIds, // -> Promise<Extra[]>
  buildExtrasMap, // -> Record<extraId, Extra & {_priceNumber:number}>
  priceToNumber, // -> "51,99" | 51.99 | undefined -> number
} from "../services/extrasService";
import { canProceed, checkActionAllowed } from "../utils/runWithGuards";
import PlusIcon from "../components/icons/PlusIcon";
import InfoIcon from "../components/icons/InfoIcon";

// Optional banner fallback
const bannerImage = process.env.PUBLIC_URL + "/header.png";

const HomePage = () => {
  // =========================
  // 1) GLOBAL / STORE STATE
  // =========================
  // Shop selection from your global store (e.g., chosen shop elsewhere)
  const shopId = useShopStore((s) => s.shop?.id);
  const userAddressShort = useShopStore((s) => s.userAddressShort); // short address, set by AddressGateModal

  // Checkout store (cart ops)
  const cartItems = useCheckoutStore((s) => s.cartItems);
  const setCartItems = useCheckoutStore((s) => s.setCartItems);
  const addItem = useCheckoutStore((s) => s.addItem);

  // BottomSheet (sticky panel for cart / store info)
  const { isOpen, title, content, openSheet, closeSheet } =
    useBottomSheetStore();

  // =========================
  // 2) LOCAL UI STATE
  // =========================
  const [isReady, setIsReady] = useState(false); // banner preloaded
  const [isShopOpen, setIsShopOpen] = useState(true); // based on openingHours

  const [searchTerm, setSearchTerm] = useState(""); // product search
  const [activeCategory, setActiveCategory] = useState(""); // currently highlighted category

  // Item modal selections
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedIngredients, setSelectedIngredients] = useState([]);
  const [selectedExtra, setSelectedExtra] = useState(""); // single-select extra
  const [showModal, setShowModal] = useState(false);

  // Address gate (middleware) state
  const [gateOpen, setGateOpen] = useState(false); // controls AddressGateModal
  const [pendingItem, setPendingItem] = useState(null); // store clicked item until address passes

  // =========================
  // 3) DATA STATE (REMOTE)
  // =========================
  const [menuItems, setMenuItems] = useState([]); // all items
  const [categoryMap, setCategoryMap] = useState({}); // id -> name
  const [ingredientsMap, setIngredientsMap] = useState({}); // id -> Ingredient
  const [shop, setShop] = useState(null); // full shop doc

  // Extras only for the currently opened item modal (keep memory small)
  const [extrasForModal, setExtrasForModal] = useState([]); // Extra[]
  // Map for fast reads in UI / price calc
  const extrasMap = useMemo(
    () => buildExtrasMap(extrasForModal),
    [extrasForModal]
  );

  // =========================
  // 4) HELPERS
  // =========================
  // top of render (before return)
  const actionGuard = checkActionAllowed({
    isShopOpen,
    userAddress: userAddressShort,
  });

  // Get the cheapest price across sizes (maps like {S:"29,99", M:"34,99", L:"39,99"})
  const getCheapestPrice = (pricesMap) => {
    if (!pricesMap || typeof pricesMap !== "object") return 0;
    const arr = Object.values(pricesMap).map(priceToNumber);
    if (!arr.length) return 0;
    return Math.min(...arr);
  };

  // Toggle ingredient by id (multi-select)
  const toggleIngredient = (id) => {
    setSelectedIngredients((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // =========================
  // 5) EFFECTS: initial fetch
  // =========================

  // Preload banner and fetch items/categories/ingredients (parallel)
  useEffect(() => {
    // Preload banner for nicer first paint
    const preload = new Image();
    preload.src = bannerImage;
    preload.onload = () => requestAnimationFrame(() => setIsReady(true));

    // Fetch menu + categories
    getAllItems().then(setMenuItems);
    getCategoryMap().then(setCategoryMap);

    // Fetch ingredients and map by id for quick lookup later
    getAllIngredients().then((ings) => {
      const map = {};
      ings.forEach((i) => (map[i.id] = i));
      setIngredientsMap(map);
    });
  }, []);

  // Fetch shop once we know shopId; also compute open/closed status
  useEffect(() => {
    if (!shopId) return;
    getShopById(shopId).then((data) => {
      setShop(data);
      if (data?.openingHours) {
        setIsShopOpen(isShopOpenNow(data.openingHours));
      }
    });
  }, [shopId]);

  // =========================
  // 6) DERIVED: categories in use
  // =========================
  const categories = useMemo(
    () => Array.from(new Set(menuItems.map((item) => item.category))),
    [menuItems]
  );

  // =========================
  // 7) ACTIONS: cart & sheets
  // =========================

  // Open store info in the bottom sheet
  const openStoreDetails = () => {
    openSheet({
      title: "Informacje o sklepie",
      content: <StoreInfo shop={shop} />,
    });
  };

  // Open cart (mobile bottom sheet)
  const openCart = () => {
    openSheet({
      title: "Twoje zamówienie",
      content: (
        <Cart
          ingredientsMap={ingredientsMap}
          extrasMap={extrasMap} // keep this so cart can show extra names/prices
          minOrderAmount={shop?.minOrderAmount}
          deliveryFee={shop?.deliveryFee}
          freeDeliveryThreshold={shop?.freeDeliveryThreshold}
          storeConfiguration={shop}
          className="shop-cart"
          onClose={closeSheet}
                isShopOpen={isShopOpen}
       userAddressShort={userAddressShort}
        />
      ),
    });
  };

  // =========================
  // 8) ADDRESS-GATED ITEM MODAL FLOW
  // =========================

  // This function performs the *actual* modal population once address is OK.
  const proceedOpenItemModal = async (item) => {
    setSelectedItem(item);
    setSelectedSize(item.sizes?.[0] || ""); // default to first size
    setSelectedIngredients(item.recommendationIds || []); // preselect recommended
    setSelectedExtra("");

    // Fetch extras just-in-time *for this item only*
    if (Array.isArray(item.extras) && item.extras.length) {
      const docs = await fetchExtrasByIds(item.extras);
      setExtrasForModal(docs);
    } else {
      setExtrasForModal([]);
    }
    setShowModal(true);
  };

  // Interceptor: if no address, open AddressGateModal; otherwise open ItemModal
  const openItemModal = async (item) => {
    if (!userAddressShort) {
      setPendingItem(item); // remember which item user clicked
      setGateOpen(true); // open the “middleware” address modal
      return;
    }
    await proceedOpenItemModal(item);
  };

  // =========================
  // 9) ADD-TO-CART HANDLER
  // =========================
  const handleAddToCart = (
    item,
    size,
    ingIds = selectedIngredients,
    extraId = selectedExtra
  ) => {
    // base size price
    const basePrice = priceToNumber(item?.prices?.[size]);

    // add-ons (ingredients)
    const ingTotal = (ingIds || []).reduce(
      (sum, id) => sum + priceToNumber(ingredientsMap[id]?.price),
      0
    );

    // extra (single-select)
    const extraPrice = extraId
      ? extrasMap[extraId]?._priceNumber ??
        priceToNumber(extrasMap[extraId]?.price)
      : 0;

    const totalPrice = (basePrice + ingTotal + extraPrice).toFixed(2);

    // cartKey defines uniqueness of a line item
    const cartKey = `${item.id}_${size}_${(ingIds || []).sort().join("_")}_${
      extraId || ""
    }`;

    const existing = cartItems.find((i) => i.cartKey === cartKey);
    if (existing) {
      // increment quantity if exact same configuration already in cart
      setCartItems(
        cartItems.map((i) =>
          i.cartKey === cartKey ? { ...i, quantity: i.quantity + 1 } : i
        )
      );
    } else {
      // create fresh line
      addItem({
        ...item,
        cartKey,
        selectedSize: size,
        selectedIngredients: ingIds,
        selectedExtra: extraId || "", // persisted in cart
        price: totalPrice, // final unit price
        quantity: 1,
      });
    }

    // reset + close modal
    setSelectedItem(null);
    setSelectedSize("");
    setSelectedIngredients([]);
    setSelectedExtra("");
    setExtrasForModal([]);
    setShowModal(false);
  };

  // =========================
  // 10) RENDER GUARDS
  // =========================
  if (!isReady || !shop) {
    return <div className="page-loader">Ładowanie...</div>;
  }

  // =========================
  const getRatingPercent = (rating) => {
    if (!rating?.average) return "—";
    const avg = parseFloat(rating.average);
    if (isNaN(avg)) return "—";
    return `${Math.round((avg / 5) * 100)}%`;
  };

  return (
    <div className="shop-page-container">
      {/* ---------- Header Banner ---------- */}
      <div className="shop-header-banner">
        <div
          className="shop-header-banner-background"
          style={{ backgroundImage: `url(${shop?.coverPhoto || bannerImage})` }}
        />
      </div>

      {/* ---------- Main Content ---------- */}
      <main className="shop-main-container" role="main">
        <section className="shop-content-section">
          {/* ===== Store Header Row ===== */}
          <header className="shop-header-info">
            <div className="shop-logo-container">
              <img
                src={shop.logo}
                alt={shop.storeName}
                className="shop-logo"
                width={80}
              />

              <div className="shop-title-wrapper">
                <div className="shop-title-container">
                  <h1
                    className="shop-title"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    {shop.storeName}
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        fontSize: "14px",
                        color: isShopOpen ? "#00a082" : "#d32f2f",
                      }}
                    >
                      <span
                        style={{
                          width: "8px",
                          height: "8px",
                          borderRadius: "50%",
                          backgroundColor: isShopOpen ? "#00a082" : "#d32f2f",
                        }}
                      />
                      {isShopOpen ? "Otwarte" : "Zamknięte"}
                    </span>
                  </h1>

                  <p className="shop-slogan">{shop.storeDescription}</p>
                </div>

                {/* Info button opens BottomSheet with StoreInfo */}
                <div
                  className="shop-info-buttons"
                  onClick={openStoreDetails}
                  role="button"
                  tabIndex={0}
                >
                  <InfoIcon />
                </div>
              </div>
            </div>

            {/* ===== Quick badges (rating / hours / delivery fee, etc.) ===== */}
            <div className="shop-badges-container">
              <div className="shop-rating-badge shop-badge">
                <img src="/icons/rating.png" alt="Ocena" width="32" />
                <span>
                  {getRatingPercent(shop.rating)} (
                  {shop.rating?.totalReviews || 0})
                </span>
              </div>

              {shop.openingHours && (
                <div className="shop-eta-badge shop-badge">
                  <img src="/icons/eta.png" alt="Godziny otwarcia" width="32" />
                  {(() => {
                    // show today's hours using your util
                    const idx = new Date().getDay();
                    const todayArr = formatOpeningHoursToday(shop.openingHours);
                    const today = todayArr[idx];
                    if (today === "Closed") return <span>Zamknięte</span>;
                    return (
                      <span>
                        {today.open} - {today.close}
                      </span>
                    );
                  })()}
                </div>
              )}

              {shop.deliveryFee !== undefined && (
                <div className="shop-service-fee-badge shop-badge">
                  <img src="/icons/dt.png" alt="Opłata za dostawę" width="32" />
                  <span>
                    {shop.deliveryFee}
                    {shop.userExperienceSettings?.currency || "zł"}
                  </span>
                </div>
              )}
            </div>
          </header>

          {/* ===== Search + Categories Row ===== */}
          <div className="view-toggle-buttons">
            {/* Search input filters only by item name (pl) for now */}
            <input
              type="text"
              className="shop-search-input"
              placeholder={`Wyszukaj w ${shop.storeName}`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />

            {/* Horizontally scrollable categories bar */}
            <div className="categories-scrollable-container">
              {categories.map((catId) => (
                <button
                  key={catId}
                  className={`category-button ${
                    activeCategory === catId ? "active" : ""
                  }`}
                  onClick={() => {
                    setActiveCategory(catId);
                    // Scroll to category anchor
                    document
                      .getElementById(`cat-${catId}`)
                      ?.scrollIntoView({ behavior: "smooth" });
                  }}
                >
                  {categoryMap[catId] || "Nieznana kategoria"}
                </button>
              ))}
            </div>
          </div>

          {/* ===== Menu Grid by Category ===== */}
          <div className="menu-section">
            {categories.map((catId) => {
              // filter items by category + search
              const filtered = menuItems.filter(
                (item) =>
                  item.category === catId &&
                  (item.name?.pl || "")
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase())
              );

              if (!filtered.length) return null;

              return (
                <div key={catId} id={`cat-${catId}`} className="menu-category">
                  <h3>{categoryMap[catId]}</h3>

                  <div className="menu-grid">
                    {filtered.map((item) => {
                      // cheapest display price across sizes
                      const displayPrice =
                        item.prices && Object.keys(item.prices).length
                          ? getCheapestPrice(item.prices).toFixed(2)
                          : null;

                      return (
                        <div key={item.id} className="menu-card">
                          {/* Image section — render only if imageUrl exists */}
                          {item?.imageUrl && (
                            <div className="image-placeholder">
                              <img
                                src={item.imageUrl}
                                alt={item?.name?.pl}
                                onError={(e) => {
                                  e.currentTarget.style.display = "none";
                                }}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                            </div>
                          )}

                          {/* Details */}
                          <div className="menu-details">
                            <h4>{item.name?.pl}</h4>
                            <p>{item.description?.pl}</p>
                            <div className="price">
                              {displayPrice != null
                                ? `${displayPrice} zł`
                                : "Brak ceny"}
                            </div>
                          </div>

                          {/* Add button */}
                          <button
                            className="add-to-btn"
                            disabled={!isShopOpen} // disable ONLY when closed
                            title={
                              !isShopOpen ? "Restauracja jest zamknięta" : ""
                            }
                            onClick={() => {
                              if (!isShopOpen) return; // guard just in case
                              // if address is missing, this will open the gate (your logic below handles it)
                              openItemModal(item);
                            }}
                            aria-label="Dodaj do koszyka"
                          >
                            <PlusIcon />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* ===== Mobile Cart Button ===== */}
          {cartItems?.length > 0 && (
            <div className="shop-cart-mobile">
              {!isShopOpen && (
                <div
                  className="shop-closed-hint"
                  role="status"
                  aria-live="polite"
                  title="Możesz przeglądać koszyk, ale złożysz zamówienie, gdy restauracja będzie otwarta."
                >
                  Restauracja jest zamknięta
                </div>
              )}

              <button
                className={`shop-cart-button ${
                  !isShopOpen ? "soft-disabled" : ""
                }`}
                onClick={openCart} // nadal pozwalamy otworzyć koszyk
                title={
                  !isShopOpen
                    ? "Restauracja jest zamknięta — możesz przeglądać koszyk, ale zamówisz, gdy będzie otwarta."
                    : ""
                }
              >
                Pokaż koszyk ({cartItems.length})
              </button>
            </div>
          )}
        </section>

        {/* ===== Desktop Cart Sidebar ===== */}
        <aside className="shop-aside-section">
          <div className="shop-cart">
            <h2>Twoje zamówienie</h2>
            <Cart
              ingredientsMap={ingredientsMap}
              extrasMap={extrasMap}
              minOrderAmount={shop?.minOrderAmount}
              deliveryFee={shop?.deliveryFee}
              freeDeliveryThreshold={shop?.freeDeliveryThreshold}
              storeConfiguration={shop}
              className="shop-cart"
              onClose={closeSheet} // only in bottom sheet version
              isShopOpen={isShopOpen}
              userAddressShort={userAddressShort}
            />
          </div>
        </aside>
      </main>

      {/* ===== Address Gate (middleware) =====
           Opens before ItemModal if user has not provided a valid address yet.
      */}
      <AddressGateModal
        isOpen={gateOpen}
        onClose={() => setGateOpen(false)}
        onPassed={async () => {
          setGateOpen(false);
          if (pendingItem) {
            const item = pendingItem;
            setPendingItem(null);
            await proceedOpenItemModal(item);
          }
        }}
      />

      {/* ===== Item Modal ===== */}
      {showModal && selectedItem && (
        <ItemModal
          // selections
          selectedItem={selectedItem}
          selectedSize={selectedSize}
          setSelectedSize={setSelectedSize}
          selectedIngredients={selectedIngredients}
          toggleIngredient={toggleIngredient}
          ingredientsMap={ingredientsMap}
          // extras (single-select)
          selectedExtra={selectedExtra}
          setSelectedExtra={setSelectedExtra}
          extrasMap={extrasMap}
          // actions
          handleAddToCart={handleAddToCart}
          closeModal={() => setShowModal(false)}
        />
      )}

      {/* ===== BottomSheet host (cart / store info) ===== */}
      <BottomSheet
        isOpen={isOpen}
        toggleBottomSheet={closeSheet}
        bottomSheetTitle={title}
      >
        {content}
      </BottomSheet>
    </div>
  );
};

export default HomePage;
